import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Sparkles, Play, CheckCircle2, AlertTriangle, ArrowRight, Minus } from "lucide-react";
import { toast } from "sonner";

const RULE_NUMBER_KEYS = {
  max_hours: "hours",
  min_hours: "hours",
  min_rest: "hours",
  max_consecutive_days: "days",
  min_consecutive_days_off: "days",
  min_shifts_per_period: "count",
  max_shifts_per_period: "count",
  min_staff: "count",
};

const HUMAN_RULE_LABEL = {
  max_hours: "Max hours per week",
  min_hours: "Min hours per week",
  min_rest: "Min rest between shifts (hours)",
  max_consecutive_days: "Max consecutive workdays",
  min_consecutive_days_off: "Min days off between blocks",
  min_shifts_per_period: "Min shifts per roster",
  max_shifts_per_period: "Max shifts per roster",
  min_staff: "Min staff (global)",
  weekend_distribution: "Fair weekend distribution",
};

export const WhatIfDialog = ({ open, onOpenChange, rosterId, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rules, setRules] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [excludeEmps, setExcludeEmps] = useState(new Set());
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setExcludeEmps(new Set());
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rRes, sRes, eRes] = await Promise.all([
        axios.get(`${API}/rules`, { withCredentials: true }),
        axios.get(`${API}/shifts`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
      ]);
      setRules(rRes.data.map(r => ({ ...r, _override: {} })));
      setShifts(sRes.data.map(s => ({ ...s, _override: {} })));
      setEmployees(eRes.data);
    } catch (e) {
      toast.error("Failed to load simulation data");
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = (apply = false) => {
    const rule_overrides = rules
      .filter(r => Object.keys(r._override || {}).length > 0)
      .map(r => ({
        rule_id: r.rule_id,
        ...(r._override.enabled !== undefined ? { enabled: r._override.enabled } : {}),
        ...(r._override.is_hard !== undefined ? { is_hard: r._override.is_hard } : {}),
        ...(r._override.value !== undefined ? { value: r._override.value } : {}),
      }));
    const shift_overrides = shifts
      .filter(s => Object.keys(s._override || {}).length > 0)
      .map(s => ({
        shift_id: s.shift_id,
        ...(s._override.min_staff !== undefined ? { min_staff: s._override.min_staff } : {}),
        ...(s._override.max_staff !== undefined ? { max_staff: s._override.max_staff } : {}),
      }));
    return {
      rule_overrides,
      shift_overrides,
      exclude_employees: Array.from(excludeEmps),
      apply,
    };
  };

  const runSimulation = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/rosters/${rosterId}/simulate`, buildPayload(false), { withCredentials: true });
      setResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally {
      setRunning(false);
    }
  };

  const applyChanges = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/rosters/${rosterId}/simulate`, buildPayload(true), { withCredentials: true });
      toast.success("Changes applied — roster regenerated");
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply changes");
    } finally {
      setApplying(false);
    }
  };

  const updateRuleValue = (idx, key, value) => {
    setRules(prev => {
      const copy = [...prev];
      const r = { ...copy[idx] };
      r._override = { ...r._override, value: { ...(r.value || {}), [key]: value } };
      copy[idx] = r;
      return copy;
    });
  };

  const updateRuleToggle = (idx, field, value) => {
    setRules(prev => {
      const copy = [...prev];
      const r = { ...copy[idx] };
      r._override = { ...r._override, [field]: value };
      copy[idx] = r;
      return copy;
    });
  };

  const updateShift = (idx, field, value) => {
    setShifts(prev => {
      const copy = [...prev];
      const s = { ...copy[idx] };
      s._override = { ...s._override, [field]: value };
      copy[idx] = s;
      return copy;
    });
  };

  const toggleExclude = (eid) => {
    setExcludeEmps(prev => {
      const next = new Set(prev);
      if (next.has(eid)) next.delete(eid); else next.add(eid);
      return next;
    });
  };

  const getRuleNumberValue = (rule) => {
    const key = RULE_NUMBER_KEYS[rule.rule_type];
    if (!key) return null;
    const v = rule._override.value !== undefined ? rule._override.value[key] : rule.value?.[key];
    return v ?? "";
  };

  const isModified = (
    rules.some(r => Object.keys(r._override || {}).length > 0) ||
    shifts.some(s => Object.keys(s._override || {}).length > 0) ||
    excludeEmps.size > 0
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="what-if-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#002FA7] dark:text-blue-400" />
            What-If Simulator
          </DialogTitle>
          <DialogDescription>
            Try rule, shift, or employee changes — see the impact before committing.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            {/* Rules */}
            <section data-testid="sim-rules-section">
              <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 mb-2">Rules</h3>
              <div className="border border-zinc-200 dark:border-zinc-800 divide-y divide-zinc-100 dark:divide-zinc-800">
                {rules.map((r, idx) => {
                  const numKey = RULE_NUMBER_KEYS[r.rule_type];
                  const enabled = r._override.enabled !== undefined ? r._override.enabled : r.enabled;
                  const isHard = r._override.is_hard !== undefined ? r._override.is_hard : r.is_hard;
                  return (
                    <div key={r.rule_id} className="flex items-center gap-3 px-3 py-2.5">
                      <Switch checked={enabled} onCheckedChange={(v) => updateRuleToggle(idx, 'enabled', v)} data-testid={`sim-rule-enabled-${idx}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${enabled ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500 line-through'}`}>
                            {HUMAN_RULE_LABEL[r.rule_type] || r.name}
                          </p>
                          <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded ${isHard ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'}`}>
                            {isHard ? 'Hard' : 'Soft'}
                          </span>
                        </div>
                      </div>
                      {numKey && (
                        <Input
                          type="number"
                          value={getRuleNumberValue(r)}
                          onChange={(e) => updateRuleValue(idx, numKey, parseInt(e.target.value) || 0)}
                          className="w-20 h-8 text-sm tabular-nums"
                          disabled={!enabled}
                          data-testid={`sim-rule-value-${idx}`}
                        />
                      )}
                      <Switch
                        checked={isHard}
                        onCheckedChange={(v) => updateRuleToggle(idx, 'is_hard', v)}
                        disabled={!enabled}
                        title="Hard / Soft"
                        data-testid={`sim-rule-hard-${idx}`}
                      />
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Shifts */}
            <section data-testid="sim-shifts-section">
              <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 mb-2">Shifts</h3>
              <div className="border border-zinc-200 dark:border-zinc-800 divide-y divide-zinc-100 dark:divide-zinc-800">
                {shifts.map((s, idx) => {
                  const minVal = s._override.min_staff !== undefined ? s._override.min_staff : s.min_staff;
                  const maxRaw = s._override.max_staff !== undefined ? s._override.max_staff : s.max_staff;
                  const maxValStr = maxRaw === null || maxRaw === undefined ? "unlimited" : String(maxRaw);
                  return (
                    <div key={s.shift_id} className="flex items-center gap-3 px-3 py-2.5">
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 flex-1 truncate">{s.name}</span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums hidden sm:block">{s.start_time}–{s.end_time}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Min</span>
                        <Select value={String(minVal || 1)} onValueChange={(v) => updateShift(idx, 'min_staff', parseInt(v, 10))}>
                          <SelectTrigger className="w-20 h-8" data-testid={`sim-shift-min-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5,6,7,8,9,10,12,15,20].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Max</span>
                        <Select
                          value={maxValStr}
                          onValueChange={(v) => updateShift(idx, 'max_staff', v === "unlimited" ? null : parseInt(v, 10))}
                        >
                          <SelectTrigger className="w-28 h-8" data-testid={`sim-shift-max-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unlimited">Unlimited</SelectItem>
                            {[1,2,3,4,5,6,7,8,9,10,12,15,20].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Employees */}
            <section data-testid="sim-employees-section">
              <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 mb-2">
                Exclude employees ({excludeEmps.size}/{employees.length})
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {employees.map(emp => {
                  const isOff = excludeEmps.has(emp.employee_id);
                  return (
                    <button
                      key={emp.employee_id}
                      onClick={() => toggleExclude(emp.employee_id)}
                      data-testid={`sim-emp-${emp.employee_id}`}
                      className={`text-xs px-2 py-1 rounded border transition-colors ${
                        isOff
                          ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 line-through border-zinc-300 dark:border-zinc-700'
                          : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                      }`}
                    >
                      {emp.name}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Run Simulation Button */}
            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={runSimulation}
                disabled={!isModified || running}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="sim-run-btn"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Run Simulation
              </Button>
              {!isModified && <span className="text-xs text-zinc-400 dark:text-zinc-500">Make at least one change above to enable simulation.</span>}
            </div>

            {/* Results */}
            {result && (
              <section data-testid="sim-results-section" className="border-t border-zinc-200 dark:border-zinc-800 pt-5">
                {result.infeasible ? (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4 rounded">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-700 dark:text-red-300">Simulation infeasible</p>
                        {result.issues?.map((i, k) => (
                          <p key={k} className="text-xs text-red-600 dark:text-red-400 mt-1">{i.title}: {i.message}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 mb-3">Comparison</h3>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="bg-zinc-50 dark:bg-zinc-800/50 p-3 rounded border border-zinc-200 dark:border-zinc-800">
                        <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">Current</p>
                        <StatRow label="Total entries" value={result.current.total_entries} />
                        <StatRow label="Days covered" value={result.current.dates_covered} />
                        <StatRow label="Per emp range" value={`${result.current.per_emp_min} – ${result.current.per_emp_max}`} />
                        <StatRow label="Per emp avg" value={result.current.per_emp_avg} />
                        <StatRow label="Max h/wk" value={`${result.current.max_hours_week}h`} />
                      </div>
                      <div className="flex items-center justify-center text-zinc-300 dark:text-zinc-600">
                        <ArrowRight className="w-6 h-6" />
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800/50">
                        <p className="text-[10px] uppercase tracking-wide text-[#002FA7] dark:text-blue-300 mb-2">Simulated</p>
                        <StatRow label="Total entries" value={result.simulated.total_entries} delta={result.delta.entries} />
                        <StatRow label="Days covered" value={result.simulated.dates_covered} />
                        <StatRow label="Per emp range" value={`${result.simulated.per_emp_min} – ${result.simulated.per_emp_max}`} />
                        <StatRow label="Per emp avg" value={result.simulated.per_emp_avg} />
                        <StatRow label="Max h/wk" value={`${result.simulated.max_hours_week}h`} delta={result.delta.max_hours_week} />
                      </div>
                    </div>
                    {result.simulated.uncovered_slots > 0 && (
                      <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">⚠ {result.simulated.uncovered_slots} slot(s) couldn't be filled in the simulated roster.</p>
                    )}
                    {result.simulated.underallocated_count > 0 && (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">⚠ {result.simulated.underallocated_count} employee(s) below min-shifts target.</p>
                    )}
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sim-cancel-btn">Cancel</Button>
          <Button
            onClick={applyChanges}
            disabled={!result || result.infeasible || applying}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="sim-apply-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Apply changes & regenerate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatRow = ({ label, value, delta }) => (
  <div className="flex items-center justify-between py-0.5">
    <span className="text-[11px] text-zinc-500 dark:text-zinc-400">{label}</span>
    <div className="flex items-center gap-1.5">
      <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{value}</span>
      {delta !== undefined && delta !== 0 && (
        <span className={`text-[10px] font-medium tabular-nums ${
          delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
        }`}>
          {delta > 0 ? '+' : ''}{delta}
        </span>
      )}
      {delta === 0 && <Minus className="w-2.5 h-2.5 text-zinc-300 dark:text-zinc-600" />}
    </div>
  </div>
);

export default WhatIfDialog;
